import React, { useMemo, useState, useCallback } from "react";
import { useLocation, useHistory, Link } from "react-router-dom";
import "../../auth.css";

const COMPLETE_RESET_URL = process.env.REACT_APP_MFA_POC_COMPLETE_RESET_URL; // MFA: backend endpoint

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function safeErrorMessage(e) {
  if (!e) return "Something went wrong. Please try again.";
  if (typeof e === "string") return e;
  if (e.message) return e.message;
  return String(e);
}

function extractApiMessage(data, fallbackMessage) {
  if (!data) return fallbackMessage;

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === "object" && parsed.message) {
        return parsed.message;
      }
    } catch {}
    return data || fallbackMessage;
  }

  if (typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }

  if (data.body) {
    if (typeof data.body === "string") {
      try {
        const parsedBody = JSON.parse(data.body);
        if (
          parsedBody &&
          typeof parsedBody === "object" &&
          typeof parsedBody.message === "string" &&
          parsedBody.message.trim()
        ) {
          return parsedBody.message.trim();
        }
      } catch {
        if (data.body.trim()) {
          return data.body.trim();
        }
      }
    }

    if (
      typeof data.body === "object" &&
      data.body !== null &&
      typeof data.body.message === "string" &&
      data.body.message.trim()
    ) {
      return data.body.message.trim();
    }
  }

  return fallbackMessage;
}

function parseApiResponseText(rawText) {
  if (!rawText || !rawText.trim()) {
    return null;
  }

  const trimmed = rawText.trim();

  try {
    return JSON.parse(trimmed);
  } catch {}

  const firstJsonObject = trimmed.indexOf("{");
  const firstJsonArray = trimmed.indexOf("[");
  let firstJsonIndex = -1;

  if (firstJsonObject >= 0 && firstJsonArray >= 0) {
    firstJsonIndex = Math.min(firstJsonObject, firstJsonArray);
  } else if (firstJsonObject >= 0) {
    firstJsonIndex = firstJsonObject;
  } else if (firstJsonArray >= 0) {
    firstJsonIndex = firstJsonArray;
  }

  if (firstJsonIndex >= 0) {
    const cleaned = trimmed.slice(firstJsonIndex);
    try {
      return JSON.parse(cleaned);
    } catch {}
  }

  return trimmed;
}

export default function MfaPocCompleteReset() {
  const history = useHistory();
  const qs = useQuery();

  // MFA: read email + token (+ optional "next") from reset link:
  //   ?u=<email>&t=<token>[&next=<return-url>]
  const emailFromLink = (qs.get("u") || "").trim();
  const tokenFromLink = (qs.get("t") || "").trim();
  const nextFromLink = (qs.get("next") || "").trim(); // NEW

  const [email, setEmail] = useState(emailFromLink);
  const [token, setToken] = useState(tokenFromLink);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  // MFA: mirror Cognito password policy (12 chars, upper, lower, number, symbol)
  const { req, allOk, matchOk, score } = useMemo(() => {
    const length = pw1.length >= 12;
    const upper = /[A-Z]/.test(pw1);
    const lower = /[a-z]/.test(pw1);
    const number = /\d/.test(pw1);
    const symbol = /[^A-Za-z0-9]/.test(pw1);
    const reqObj = { length, upper, lower, number, symbol };

    const allOkVal =
      reqObj.length && reqObj.upper && reqObj.lower && reqObj.number && reqObj.symbol;
    const matchOkVal = pw1.length > 0 && pw1 === pw2;
    const scoreVal =
      (reqObj.length ? 1 : 0) +
      (reqObj.upper ? 1 : 0) +
      (reqObj.lower ? 1 : 0) +
      (reqObj.number ? 1 : 0) +
      (reqObj.symbol ? 1 : 0);

    return { req: reqObj, allOk: allOkVal, matchOk: matchOkVal, score: scoreVal };
  }, [pw1, pw2]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!token) {
        setError(
          "Reset link is missing or invalid. Please copy the entire reset link from your email and open it again."
        );
        return;
      }
      if (!email || !token || !pw1 || !pw2) {
        setError("Please fill in all fields.");
        return;
      }
      if (!allOk) {
        setError("Your password does not meet the requirements.");
        return;
      }
      if (!matchOk) {
        setError("Passwords must match.");
        return;
      }
      if (!COMPLETE_RESET_URL) {
        setError(
          "Reset service is not configured. Please contact support."
        ); // MFA: missing env guard
        return;
      }

      setBusy(true);
      setError("");
      setInfo("");

      try {
        const trimmedEmail = email.trim();
        const trimmedToken = token.trim();

        const payload = {
          // MFA: original direct Lambda payload (CompletePasswordReset expects this)
          email: trimmedEmail,
          token: trimmedToken,
          new_password: pw1,

          // MFA: public ACCOUNT API payload shape (matches OpenAPI-style fields)
          // This lets us call /auth/complete-password-reset via the Account API too.
          username: trimmedEmail,
          resetToken: trimmedToken,
          newPassword: pw1,
        };

        const res = await fetch(COMPLETE_RESET_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        let data = null;
        let rawText = "";
        try {
          rawText = await res.text();
          data = parseApiResponseText(rawText);
        } catch {
          // ignore response parse errors
        }

        if (!res.ok) {
          const msg = extractApiMessage(
            data,
            "This link is invalid or has expired. Please request a new reset."
          );
          setError(msg);
          return;
        }

        const msg = extractApiMessage(
          data,
          "Password reset successful, please sign in."
        );
        setInfo(msg);

        // Redirect after success:
        //   - If backend supplied a ?next=... param (PWD_RESET_RETURN_URL),
        //     send the user there (full-page redirect).
        //   - Otherwise, fall back to original behaviour: Account /signin
        const target = nextFromLink;
        if (target) {
          window.location.assign(target);
        } else {
          // MFA: keep simple; redirect with prefilled email
          history.replace(
            `/signin?email=${encodeURIComponent(trimmedEmail)}`
          );
        }
      } catch (ex) {
        setError(safeErrorMessage(ex));
      } finally {
        setBusy(false);
      }
    },
    [token, email, pw1, pw2, allOk, matchOk, nextFromLink, history]
  );

  const ReqItem = ({ ok, children }) => (
    <li className={`ph-req ${ok ? "ok" : "bad"}`}>
      <span aria-hidden="true" style={{ display: "inline-block", width: 18 }}>
        {ok ? "✓" : "✕"}
      </span>
      <span>{children}</span>
    </li>
  );

  return (
    <div className="ph-auth-wrap">
      <div className="ph-auth-card">
        <h1 className="ph-auth-title">MFA: Complete Reset</h1>

        {info && (
          <div className="ph-note" role="status">
            {info}
          </div>
        )}
        {error && (
          <div className="ph-error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit}>
          <label className="ph-label" htmlFor="mfa3-email">
            Email
          </label>
          <input
            id="mfa3-email"
            className="ph-field"
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            disabled={busy}
          />

          <div style={{ display: "none" }}>
            <label className="ph-label" htmlFor="mfa3-token">
              Reset token
            </label>
            <input
              id="mfa3-token"
              className="ph-field"
              type="text"
              placeholder="Token from reset link"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={busy}
            />
          </div>

          <label className="ph-label" htmlFor="mfa3-new">
            New Password
          </label>
          <div className="ph-field-wrap">
            <input
              id="mfa3-new"
              className={`ph-field ${pw1 && !allOk ? "invalid" : ""}`}
              type={showPw1 ? "text" : "password"}
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              autoComplete="new-password"
              placeholder="Enter a new password"
            />
            <button
              type="button"
              className="ph-toggle"
              onClick={() => setShowPw1((s) => !s)}
              aria-label={showPw1 ? "Hide password" : "Show password"}
              aria-pressed={showPw1}
              disabled={busy}
            >
              {showPw1 ? "Hide" : "Show"}
            </button>
          </div>

          {/* MFA-POC: simple strength meter */}
          <div className="ph-strength" aria-hidden="true">
            <div
              className="ph-strength-bar"
              style={{ width: `${score * 20}%` }}
            />
          </div>

          <ul
            className="ph-req-list"
            aria-live="polite"
            aria-atomic="true"
            style={{ margin: "8px 0 12px" }}
          >
            <ReqItem ok={req.length}>At least 12 characters</ReqItem>
            <ReqItem ok={req.upper}>Contains an uppercase letter (A–Z)</ReqItem>
            <ReqItem ok={req.lower}>Contains a lowercase letter (a–z)</ReqItem>
            <ReqItem ok={req.number}>Contains a number (0–9)</ReqItem>
            <ReqItem ok={req.symbol}>Contains a symbol (e.g., !@#$%)</ReqItem>
          </ul>

          <label className="ph-label" htmlFor="mfa3-new2">
            Confirm New Password
          </label>
          <div className="ph-field-wrap">
            <input
              id="mfa3-new2"
              className="ph-field"
              type={showPw2 ? "text" : "password"}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              autoComplete="new-password"
              placeholder="Re-enter new password"
              disabled={busy}
            />
            <button
              type="button"
              className="ph-toggle"
              onClick={() => setShowPw2((s) => !s)}
              aria-label={showPw2 ? "Hide password" : "Show password"}
              aria-pressed={showPw2}
              disabled={busy}
            >
              {showPw2 ? "Hide" : "Show"}
            </button>
          </div>

          <div
            className={`ph-req ${matchOk ? "ok" : "bad"}`}
            aria-live="polite"
            style={{ margin: "6px 0 12px" }}
          >
            <span
              aria-hidden="true"
              style={{ display: "inline-block", width: 18 }}
            >
              {matchOk ? "✓" : "✕"}
            </span>
            Passwords match
          </div>

          <button
            className="ph-button"
            type="submit"
            disabled={busy || !email || !allOk || !matchOk}
          >
            {busy ? "Saving…" : "Set new password"}
          </button>
        </form>

        <p className="ph-hint" style={{ marginTop: 12 }}>
          <Link className="ph-link" to="/signin">
            Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}